import {
  ArrowLeftOutlined,
  CloseOutlined,
  FormOutlined,
  MenuOutlined,
  RobotOutlined,
  SettingOutlined,
  UserOutlined,
} from '@ant-design/icons'
import {
  Avatar,
  Button,
  Col,
  Drawer,
  Flex,
  Layout,
  Menu,
  Row,
  Space,
  Typography,
} from 'antd'
import { Link } from 'react-router-dom'
import { useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { extractSectionFromPath } from '../../helpers/RouterHelper'
import { ItemType, MenuItemType } from 'antd/es/menu/interface'
import { useAppSelector } from '../../store/hooks'
import { selectSelectedProfile } from '../../store/ProfileReducer'
import i18n from '../../i18n/setupLocalization'
import LocalizationKeys from '../../i18n/LocalizationKeys'
import { useHypertune } from '../../generated/hypertune.react'
import { Subscription } from '../../components/subscription/Subscription'
import { Automation } from '../../components/automation/Automation'

const { Text } = Typography
const { Header } = Layout

interface Props {
  subItems?: ItemType<MenuItemType>[] | undefined
}

const ProfileHeader = ({ subItems }: Props) => {
  const { pathname } = useLocation()
  const hypertune = useHypertune()
  const profile = useAppSelector(selectSelectedProfile())

  const [selectedKey, setSelectedKey] = useState<string>('automations')
  const [selectedSubKey, setSelectedSubKey] = useState<string>('contacts')
  const [drawerVisible, setDrawerVisible] = useState<boolean>(false)

  const headerStyle: React.CSSProperties = {
    justifyContent: 'space-between',
    display: 'flex',
    paddingRight: 20,
    paddingLeft: 20,
  }

  const subHeaderStyle: React.CSSProperties = {
    backgroundColor: '#e6e6e6',
    justifyContent: 'space-between',
    display: 'flex',
    paddingRight: 20,
    paddingLeft: 20,
    height: 50,
    lineHeight: '50px',
  }

  useEffect(() => {
    setSelectedKey(extractSectionFromPath(pathname, 3))
    setSelectedSubKey(extractSectionFromPath(pathname, 2))
  }, [pathname])

  const menuItems: ItemType<MenuItemType>[] = [
    {
      key: 'automations',
      onClick: () => setDrawerVisible(false),
      label: (
        <Link
          to={
            profile && profile.firstAutomationUlid
              ? `automations/${profile.firstAutomationUlid}`
              : 'automations'
          }
        >
          {i18n.t(LocalizationKeys.Layout.ProfileHeader.Automation)}
        </Link>
      ),
      icon: <RobotOutlined />,
    },
    {
      key: 'contacts',
      onClick: () => setDrawerVisible(false),
      label: (
        <Link to={'contacts'}>
          {i18n.t(LocalizationKeys.Layout.ProfileHeader.Audience)}
        </Link>
      ),
      icon: <UserOutlined />,
    },
    ...[
      hypertune.campaign({ fallback: false })
        ? {
            key: 'campagnes',
            onClick: () => setDrawerVisible(false),
            label: (
              <Link to={'campagnes'}>
                {i18n.t(LocalizationKeys.Layout.ProfileHeader.Campaigns)}
              </Link>
            ),
            icon: <FormOutlined />,
          }
        : undefined,
    ].filter((e) => e !== undefined),

    // {
    //   key: 'activities',
    //   label: <Link to={'activities'}>Activity</Link>,
    //   icon: <HistoryOutlined />,
    // },
    {
      key: 'settings',
      onClick: () => setDrawerVisible(false),
      label: (
        <Link to={'settings'}>
          {i18n.t(LocalizationKeys.Layout.ProfileHeader.Settings)}
        </Link>
      ),
      icon: <SettingOutlined />,
    },
  ]

  return (
    <div className={'fixed z-20 w-full'}>
      <Header style={headerStyle} className="bg-header w-full">
        <Row justify="space-between" style={{ width: '100%' }}>
          <Col>
            <Space direction="horizontal">
              <Link to={'/'}>
                <Space>
                  <ArrowLeftOutlined style={{ color: 'white' }} />
                </Space>
              </Link>
              <Space className="logo-container">
                <Link to="/">
                  <Space direction="horizontal">
                    <Avatar size={45} src={profile?.instagram.profilePicture} />
                    <Text style={{ color: 'white' }} strong>
                      {profile?.name}
                    </Text>
                  </Space>
                </Link>
              </Space>
            </Space>
          </Col>
          <Col flex="auto">
            <Flex
              className="desktop-menu w-full"
              justify="space-between"
              align="center"
            >
              <Menu
                activeKey={selectedKey}
                mode="horizontal"
                className="bg-header menu-item"
                items={menuItems}
                style={{ flexGrow: 1 }}
              />
              <Automation.StatusUpdator />
            </Flex>
          </Col>
        </Row>
        <Flex className="mobile-menu" align="center">
          <Button
            type="primary"
            icon={<MenuOutlined />}
            onClick={() => setDrawerVisible(true)}
          />
          <Drawer
            closeIcon={<CloseOutlined style={{ color: 'white' }} />}
            styles={{ header: { background: '#a58aeb', height: 64 } }}
            title={
              <Space className="w-full" style={{ justifyContent: 'flex-end' }}>
                <Automation.StatusUpdator />
              </Space>
            }
            placement="right"
            onClose={() => setDrawerVisible(false)}
            open={drawerVisible}
          >
            <Menu
              mode="vertical"
              style={{ border: 'none' }}
              activeKey={selectedKey}
              items={menuItems}
            />
          </Drawer>
        </Flex>
      </Header>
      {subItems && (
        <Header style={subHeaderStyle}>
          <div
            style={{
              maxWidth: 1200,
              width: '100%',
              margin: 'auto',
            }}
          >
            <Menu
              style={{ background: '#e6e6e6', border: 'none' }}
              className="d-flex-lg"
              mode="horizontal"
              selectedKeys={[selectedSubKey]}
              items={subItems}
            />
          </div>
        </Header>
      )}
      <Subscription.Banner subscription={profile?.subscription} />
    </div>
  )
}

export default ProfileHeader
